import React, { useEffect, useState } from 'react';
import Toast from '../../../components/Common/Toast';
import { machineryService } from '../../../services/machineryService';

const MachineryCategories = () => {
  const [vehicleLines, setVehicleLines] = useState([]);
  const [machineryLines, setMachineryLines] = useState([]);
  const [vehicleInput, setVehicleInput] = useState('');
  const [machineryInput, setMachineryInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [savingMachine, setSavingMachine] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadCategories = async () => {
    setLoading(true);
    const response = await machineryService.getCategories();
    if (!response.success) {
      setToast({
        isOpen: true,
        message: response.message || 'No se pudieron cargar las categorías',
        type: 'error',
      });
      setLoading(false);
      return;
    }

    const rows = Array.isArray(response.data) ? response.data : [];
    setVehicleLines(rows.filter((item) => item.scope === 'vehicle'));
    setMachineryLines(rows.filter((item) => item.scope === 'machine'));
    setLoading(false);
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const addVehicleLine = async () => {
    const value = vehicleInput.trim();
    if (!value) return;
    setSavingVehicle(true);
    const response = await machineryService.createCategory({ name: value, scope: 'vehicle' });
    if (!response.success) {
      setToast({
        isOpen: true,
        message: response.message || 'No se pudo agregar la línea de vehículo',
        type: 'error',
      });
      setSavingVehicle(false);
      return;
    }
    await loadCategories();
    setVehicleInput('');
    setSavingVehicle(false);
    setToast({
      isOpen: true,
      message: response.message || 'Línea de vehículo agregada',
      type: 'success',
    });
  };

  const addMachineryLine = async () => {
    const value = machineryInput.trim();
    if (!value) return;
    setSavingMachine(true);
    const response = await machineryService.createCategory({ name: value, scope: 'machine' });
    if (!response.success) {
      setToast({
        isOpen: true,
        message: response.message || 'No se pudo agregar la línea de maquinaria',
        type: 'error',
      });
      setSavingMachine(false);
      return;
    }
    await loadCategories();
    setMachineryInput('');
    setSavingMachine(false);
    setToast({
      isOpen: true,
      message: response.message || 'Línea de maquinaria agregada',
      type: 'success',
    });
  };

  const removeLine = async (item, typeLabel) => {
    const response = await machineryService.deleteCategory(item.id);
    if (!response.success) {
      setToast({
        isOpen: true,
        message: response.message || `No se pudo eliminar la línea de ${typeLabel}`,
        type: 'error',
      });
      return;
    }
    await loadCategories();
    setToast({
      isOpen: true,
      message: response.message || `Línea de ${typeLabel} eliminada`,
      type: 'success',
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Categorías de maquinarias</h1>
        <p className="text-gray-600 mt-1">
          Configure líneas para vehículos y maquinarias. Este apartado es independiente del campo tipo.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Líneas de vehículos</h2>
            <p className="text-sm text-gray-500 mt-1">
              Aquí se registrarán las líneas/categorías para vehículos.
            </p>
          </div>
          <div className="p-5">
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={vehicleInput}
                onChange={(e) => setVehicleInput(e.target.value)}
                placeholder="Ej: Camiones livianos"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                type="button"
                onClick={addVehicleLine}
                disabled={savingVehicle}
                className="px-3 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm rounded-md"
              >
                {savingVehicle ? 'Guardando...' : 'Agregar'}
              </button>
            </div>
            {!loading && vehicleLines.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-sm text-gray-500 text-center">
                Sin datos configurados por el momento.
              </div>
            ) : loading ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-sm text-gray-500 text-center">
                Cargando categorías...
              </div>
            ) : (
              <ul className="space-y-2">
                {vehicleLines.map((line) => (
                  <li key={line.id} className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2">
                    <span className="text-sm text-gray-800">{line.name}</span>
                    <button
                      type="button"
                      onClick={() => removeLine(line, 'vehículo')}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Eliminar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Líneas de maquinarias</h2>
            <p className="text-sm text-gray-500 mt-1">
              Aquí se registrarán las líneas/categorías para maquinarias.
            </p>
          </div>
          <div className="p-5">
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={machineryInput}
                onChange={(e) => setMachineryInput(e.target.value)}
                placeholder="Ej: Excavadoras"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                type="button"
                onClick={addMachineryLine}
                disabled={savingMachine}
                className="px-3 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm rounded-md"
              >
                {savingMachine ? 'Guardando...' : 'Agregar'}
              </button>
            </div>
            {!loading && machineryLines.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-sm text-gray-500 text-center">
                Sin datos configurados por el momento.
              </div>
            ) : loading ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-sm text-gray-500 text-center">
                Cargando categorías...
              </div>
            ) : (
              <ul className="space-y-2">
                {machineryLines.map((line) => (
                  <li key={line.id} className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2">
                    <span className="text-sm text-gray-800">{line.name}</span>
                    <button
                      type="button"
                      onClick={() => removeLine(line, 'maquinaria')}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Eliminar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ isOpen: false, message: '', type: 'info' })}
      />
    </div>
  );
};

export default MachineryCategories;

